import type { BulkLoads, BulkTags, FormData } from '../../types'

import { timeWindowUtils } from '@/utils'
import { geo } from '@/server-data'
import { store } from '@/store'
import { NONE_OPTION } from '../../../../constants'

export function computeLoadsDelta(loads: BulkLoads): Record<string, number | null> {
  return Object.values(loads.exact).reduce<Record<string, number | null>>((acc, curr) => {
    if (curr.mixed) return acc

    if (curr.value === null) {
      return acc
    }

    acc[curr.name] = Math.trunc(curr.value * 100) // decimals over the 2nd figure will be cut out
    return acc
  }, {})
}

function computeLoadsDeltaWithRemovals(
  loads: BulkLoads,
  orders: uui.domain.client.rm.ExtendedOrderStep[],
): Record<string, number | null> {
  const delta = computeLoadsDelta(loads)

  const currentLoadNames = new Set([...Object.keys(loads.exact), ...Object.keys(loads.partial)])

  for (const order of orders) {
    for (const loadName of Object.keys(order.order.loads)) {
      if (!currentLoadNames.has(loadName)) {
        delta[loadName] = null // null deletes the load server-side
      }
    }
  }

  return delta
}

function toServerLocation(clientLocation: uui.domain.client.Location): uui.domain.server.Location {
  const latLng = geo.latLngToJSON(clientLocation.latLng)

  return {
    ...clientLocation,
    latLng,
    status: 'OK',
    rooftopLatLng: clientLocation.rooftopLatLng
      ? geo.latLngToJSON(clientLocation.rooftopLatLng)
      : undefined,
  }
}

function computeTags(tags: BulkTags) {
  return Object.values(tags.tags).reduce<Record<string, string | null>>((acc, curr) => {
    if (curr.status === 'exact') {
      acc[curr.name] = curr.name
    }

    if (curr.status === 'deleted') {
      acc[curr.name] = null
    }

    return acc
  }, {})
}

function parseServiceTime(value: string, noneValue: string = '') {
  if (value === noneValue) return 0
  const [hours, minutes] = value.split(':')
  return parseInt(hours) * 3600 + parseInt(minutes) * 60
}

export function computeBulkOrderChanges(
  formData: FormData,
  orders: uui.domain.client.rm.ExtendedOrderStep[],
): uui.domain.ui.forms.BulkOrderChanges {
  const {
    eligibility,
    forceVehicleId,
    priority,
    loads,
    notes,
    type,
    serviceTimeSec,
    tagsIn,
    tagsOut,
    email,
    location,
    phone,
    timeWindows,
    companyId,
  } = formData

  const { workingDayStartSec } = store.getState().domain.publicData.territory

  const ids: uui.domain.ui.forms.BulkOrderChanges['ids'] = orders.map(o => ({
    orderId: o.order.id,
    type: o.orderStepType === 'p' ? 'pickup' : 'delivery',
  }))

  const orderData: uui.domain.ui.forms.BulkOrderData = {
    eligibility: eligibility.status === 'exact' ? eligibility.value : undefined,
    forceVehicleId:
      forceVehicleId.status === 'exact'
        ? forceVehicleId.value === ''
          ? null
          : forceVehicleId.value
        : undefined,
    priority:
      priority.status === 'exact'
        ? (parseInt(priority.value) as uui.domain.server.rm.Order['priority'])
        : undefined,
    loadsDelta: computeLoadsDeltaWithRemovals(loads, orders),
    companyId:
      companyId.status === 'exact'
        ? companyId.value === NONE_OPTION
          ? null
          : companyId.value
        : undefined,
  }

  const serverTimeWindows =
    timeWindows.status === 'exact'
      ? (timeWindows.value.map((tw: uui.domain.rm.TimeWindow | undefined) => {
          if (!tw) return undefined

          return timeWindowUtils.formatTimeWindowForRPC(
            timeWindowUtils.normalizeTimeWindow(tw, workingDayStartSec),
            workingDayStartSec,
          )
        }) as uui.domain.ui.forms.BulkOrderStepData['timeWindows'])
      : undefined

  const orderStepData: uui.domain.ui.forms.BulkOrderStepData = {
    notes: notes.status === 'exact' ? notes.value : undefined,
    email: email.status === 'exact' ? email.value : undefined,
    phone: phone.status === 'exact' ? phone.value : undefined,
    timeWindows: serverTimeWindows,
    serviceTimeSec:
      serviceTimeSec.status === 'exact' ? parseServiceTime(serviceTimeSec.value) : undefined,
    type: type.status === 'exact' ? type.value : undefined,
    location: location.status === 'exact' ? toServerLocation(location.value) : undefined,
    tagsIn: computeTags(tagsIn),
    tagsOut: computeTags(tagsOut),
  }

  return {
    ids,
    orderData,
    orderStepData,
  }
}
